import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Divider,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, Delete, ListAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcRequisitionApi, type EpcRequisition } from '../../api/epcProcurement.api';
import { epcBoqApi } from '../../api/epcBoq.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'approved' ? 'success' : s === 'submitted' ? 'info' : s === 'rejected' ? 'error' : s === 'ordered' ? 'warning' : 'default';
export const stageColor = (s?: string | null): 'default' | 'info' | 'success' | 'error' | 'warning' => {
  if (!s) return 'default';
  if (s === 'Material Received') return 'success';
  if (/Rejected/i.test(s)) return 'error';
  if (s === 'Request Accepted' || s === 'Dispatched' || s === 'Material Received (partial)') return 'info';
  if (s === 'Procurement in process' || s === 'Awaiting procurement' || s === 'Pending approval') return 'warning';
  return 'default';
};

export function EpcRequisitionsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-requisitions', page, limit, pid], queryFn: () => epcRequisitionApi.list({ page, limit, projectId: pid }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-requisitions'] });

  const columns: Column<EpcRequisition>[] = [
    { key: 'requisitionCode', label: 'Requisition', render: (r) => r.requisitionCode },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ? `${r.projectName}${r.projectCode ? ` (${r.projectCode})` : ''}` : `#${r.projectId}` },
    { key: 'siteName', label: 'Site', render: (r) => r.siteName ? `${r.siteName}${r.siteCode ? ` (${r.siteCode})` : ''}` : '—' },
    { key: 'stateName', label: 'State', render: (r) => r.stateName ?? '—' },
    { key: 'regionName', label: 'Region', render: (r) => r.regionName ?? '—' },
    { key: 'raisedByName', label: 'Raised By', render: (r) => r.raisedByName ?? '—' },
    { key: 'createdAt', label: 'Request Date', render: (r) => new Date(r.createdAt).toLocaleDateString('en-IN') },
    { key: 'requiredByDate', label: 'Required By', render: (r) => (r.requiredByDate ? new Date(r.requiredByDate).toLocaleDateString('en-IN') : '—') },
    { key: 'procurementStage', label: 'Stage', render: (r) => <Chip size="small" label={r.procurementStage ?? r.status} color={stageColor(r.procurementStage)} /> },
  ];

  return (
    <>
      <PageHeader title="EPC Requisition Request" subtitle="Materials needed for a project — seeded from an approved BOQ, then approved for ordering."
        action={hasPermission('epc_requisition.create') && (
          <Button startIcon={<Add />} variant="contained" disabled={!pid} onClick={() => { setError(null); setCreateOpen(true); }}>New Requisition Request</Button>
        )} />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><ListAlt fontSize="small" /></IconButton></Tooltip>} />

      <CreateDialog open={createOpen} projectId={pid} onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />
      {detailId && <RequisitionDetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

function CreateDialog({ open, projectId, onClose, onDone, onError, error }: {
  open: boolean; projectId?: number; onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [title, setTitle] = useState('');
  const [requiredByDate, setRequiredByDate] = useState('');
  const [boqId, setBoqId] = useState('');
  const { data: boqs } = useQuery({
    queryKey: ['epc-boqs', 'approved', projectId],
    queryFn: () => epcBoqApi.list({ projectId, status: 'approved', limit: 100 }),
    enabled: open && Boolean(projectId),
  });
  const mut = useMutation({
    mutationFn: () => epcRequisitionApi.create({ projectId: projectId!, title: title || undefined, requiredByDate: requiredByDate || undefined, boqId: boqId ? Number(boqId) : undefined }),
    onSuccess: (r) => { setTitle(''); setRequiredByDate(''); setBoqId(''); onDone(r.id); },
    onError: (e) => onError(extractApiError(e)),
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Requisition — Project #{projectId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextField select label="Seed from approved BOQ (optional)" size="small" value={boqId} onChange={(e) => setBoqId(e.target.value)}>
            <MenuItem value="">— none (add items manually) —</MenuItem>
            {(boqs?.data ?? []).map((b) => <MenuItem key={b.id} value={b.id}>{b.boqCode} — {inr(b.totalAmount)}</MenuItem>)}
          </TextField>
          <TextField label="Required by" type="date" size="small" InputLabelProps={{ shrink: true }} value={requiredByDate} onChange={(e) => setRequiredByDate(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

export function RequisitionDetailDialog({ id, onClose, onChanged, onToast, procurementMode = false }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void; procurementMode?: boolean }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: req } = useQuery({ queryKey: ['epc-requisition', id], queryFn: () => epcRequisitionApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-requisition', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  const [it, setIt] = useState({ description: '', unit: '', quantity: '', estimatedRate: '' });
  const addItem = useMutation({
    mutationFn: () => epcRequisitionApi.addItem(id, { description: it.description, unit: it.unit || undefined, quantity: Number(it.quantity), estimatedRate: it.estimatedRate ? Number(it.estimatedRate) : undefined }),
    onSuccess: () => { setIt({ description: '', unit: '', quantity: '', estimatedRate: '' }); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });

  if (!req) return null;
  const isDraft = req.status === 'draft';
  // Procurement review (accept/reject/edit) is available ONLY in the Procurement Review screen,
  // not on the Requisition Request page.
  const inProcReview = procurementMode && req.status === 'approved' && req.procurementStatus !== 'accepted';
  const canEdit = (isDraft || inProcReview) && hasPermission('epc_requisition.update');
  const canReview = procurementMode && req.status === 'approved' && req.procurementStatus === 'pending' && hasPermission('epc_requisition.approve');

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{req.requisitionCode} <Chip size="small" sx={{ ml: 1 }} label={req.status} color={statusColor(req.status)} />
        {req.status === 'approved' && req.procurementStatus && req.procurementStatus !== 'pending' && (
          <Chip size="small" sx={{ ml: 1 }} label={`Procurement: ${req.procurementStatus}`} color={req.procurementStatus === 'accepted' ? 'success' : 'error'} />
        )}
        <Typography variant="body2" color="text.secondary">
          {[
            req.clientName,
            req.projectName ? `${req.projectName}${req.projectCode ? ` (${req.projectCode})` : ''}` : `Project #${req.projectId}`,
            req.siteName ? `${req.siteName}${req.siteCode ? ` (${req.siteCode})` : ''}` : null,
          ].filter(Boolean).join(' · ')}{req.boqId ? ` · from BOQ #${req.boqId}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {req.status === 'rejected' && req.remarks && <Alert severity="warning" sx={{ mb: 1 }}>Rejected: {req.remarks}</Alert>}
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Description</TableCell><TableCell align="right">BOQ Qty</TableCell><TableCell align="right">Reconfirm Qty</TableCell>
            <TableCell>Verification</TableCell><TableCell>Unit</TableCell>
            <TableCell align="right">Est. Rate</TableCell><TableCell align="right">Amount</TableCell>{canEdit && <TableCell />}
          </TableRow></TableHead>
          <TableBody>
            {(req.items ?? []).map((row) => {
              const verified = row.reconfirmQty != null && Number(row.reconfirmQty) === Number(row.quantity);
              return (
              <TableRow key={row.id}>
                <TableCell>{row.description}</TableCell>
                <TableCell align="right">{row.quantity}</TableCell>
                <TableCell align="right">
                  {canEdit ? (
                    <TextField size="small" variant="standard" type="number" sx={{ width: 90 }}
                      defaultValue={row.reconfirmQty ?? ''}
                      onBlur={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== (row.reconfirmQty ?? null)) act(() => epcRequisitionApi.updateItem(id, row.id, { reconfirmQty: v }), 'Reconfirm qty updated'); }} />
                  ) : (row.reconfirmQty ?? '—')}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={verified ? 'success' : 'warning'} label={verified ? 'Approve' : 'Need to verify'} />
                </TableCell>
                <TableCell>{row.unit ?? '—'}</TableCell>
                <TableCell align="right">{inr(row.estimatedRate)}</TableCell><TableCell align="right">{inr(row.amount)}</TableCell>
                {canEdit && <TableCell><IconButton size="small" color="error" onClick={() => act(() => epcRequisitionApi.removeItem(id, row.id), 'Item removed')}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>
              );
            })}
            {(req.items ?? []).length === 0 && <TableRow><TableCell colSpan={canEdit ? 8 : 7}><Typography variant="body2" color="text.secondary">No items yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>

        {canEdit && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField size="small" label="Description" value={it.description} onChange={(e) => setIt({ ...it, description: e.target.value })} sx={{ minWidth: 200 }} />
              <TextField size="small" label="Qty" type="number" value={it.quantity} onChange={(e) => setIt({ ...it, quantity: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Unit" value={it.unit} onChange={(e) => setIt({ ...it, unit: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Est. Rate" type="number" value={it.estimatedRate} onChange={(e) => setIt({ ...it, estimatedRate: e.target.value })} sx={{ width: 110 }} />
              <Button startIcon={<Add />} variant="outlined" disabled={!it.description || !it.quantity || addItem.isPending} onClick={() => addItem.mutate()}>Add</Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isDraft && hasPermission('epc_requisition.delete') && <Button color="error" onClick={() => act(() => epcRequisitionApi.remove(id).then(() => onClose()), 'Requisition deleted')}>Delete</Button>}
        {isDraft && hasPermission('epc_requisition.submit') && <Button variant="contained" onClick={() => act(() => epcRequisitionApi.submit(id), 'Submitted for approval')}>Submit</Button>}
        {req.status === 'submitted' && hasPermission('epc_requisition.approve') && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcRequisitionApi.reject(id, r), 'Rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcRequisitionApi.approve(id), 'Approved — sent to procurement')}>Approve</Button>
          </>
        )}
        {canReview && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Reason for sending back?') ?? undefined; act(() => epcRequisitionApi.procurementReview(id, 'reject', r), 'Requisition rejected by procurement'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcRequisitionApi.procurementReview(id, 'accept'), 'Accepted — ready to order')}>Accept</Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
